import AxiosInstance from "../services/networkServices";

/**
 * Daily unique apps (device opens) + account registrations, with
 * all-time totals for both (Conversion page). Newer backends also break
 * unique apps down by device model. Older backends omit registrations
 * and/or the by-model fields.
 * params: { timezone: "EST"|"UTC", startDate?: "YYYY-MM-DD", endDate?: "YYYY-MM-DD" }
 * response data: { timezone, startDate, endDate,
 *   daily: [{ date, uniqueDevices, registrations,
 *             byModel: [{ model, uniqueDevices }] }],
 *   totalUniqueDevices, totalRegistrations,
 *   totalByModel: [{ model, uniqueDevices }] }
 */
export const getDeviceOpenStats = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/device-opens", {
    params,
  });
  return res.data;
};

/**
 * Tutorial completion stats (Conversion page): per tutorial type (vsFeed,
 * profile, home, launchpad), accounts that completed/skipped it per day plus
 * all-time totals. The profile tutorial has no skip path, so its skipped
 * counts are always 0.
 * params: { timezone: "EST"|"UTC", startDate?: "YYYY-MM-DD", endDate?: "YYYY-MM-DD" }
 * response data: { timezone, startDate, endDate,
 *   tutorials: [{ type, label, daily: [{ date, completed, skipped }],
 *                 totals: { completed, skipped } }] }
 */
export const getTutorialStats = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/tutorial-stats", {
    params,
  });
  return res.data;
};

/**
 * Engagement signals per day (Conversion page): every signal count plus
 * `activeUsers`, the DISTINCT users that did any of them (or signed up) that
 * day. Follows of @pagent are excluded from `follows` and from `activeUsers`.
 * `totals` covers the whole selected range (totals.activeUsers is
 * distinct users across the range, not a sum of the daily values).
 * params: { timezone: "EST"|"UTC", startDate?: "YYYY-MM-DD", endDate?: "YYYY-MM-DD" }
 * response data: { timezone, startDate, endDate,
 *   daily: [{ date, activeUsers, signups, logins, votes, posts, comments,
 *             likes, follows, matchesStarted }],
 *   totals: { activeUsers, signups, logins, votes, posts, comments, likes,
 *             follows, matchesStarted } }
 */
export const getEngagementStats = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/engagement-stats", {
    params,
  });
  return res.data;
};

/**
 * Theme engagement (Reports → Theme Engagement): one row per weekly theme
 * overlapping / created in the range — title, banner, users who opened
 * voting prefs (saw the theme), first-vs-multi that America/New_York week.
 * params: { timezone, startDate?, endDate? }
 */
export const getThemeEngagementStats = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/theme-engagement", {
    params,
  });
  return res.data;
};

/**
 * Weekly growth accounting / leaky-bucket flows (Conversion page): per ISO
 * week (Monday-start), distinct active users split into new / retained /
 * resurrected, users churned from the prior week, quickRatio =
 * (new + resurrected) / churned (< 1 means the bucket leaks; null when
 * nothing churned) and net = new + resurrected - churned. Weeks come newest
 * first; the current week is flagged isPartial.
 * params: { timezone: "EST"|"UTC", startDate?: "YYYY-MM-DD", endDate?: "YYYY-MM-DD" }
 * response data: { timezone, startDate, endDate,
 *   weeks: [{ weekStart, weekEnd, isPartial, active, newUsers, retained,
 *             resurrected, churned, quickRatio, net }] }
 */
export const getGrowthAccounting = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/growth-accounting", {
    params,
  });
  return res.data;
};

/**
 * Stickiness (Conversion page, Retention tab): rolling DAU / WAU / MAU per
 * day with DAU/MAU and WAU/MAU, plus the creator rate as of endDate.
 * population "activated" (default) = users with 5+ votes, counted from the
 * day of their 5th vote; "all" = every account.
 * params: { timezone, startDate?, endDate?, population? }
 * response data: { timezone, population, activationVotes, startDate, endDate,
 *   days: [{ date, dau, wau, mau, dauMau, wauMau }] (oldest first),
 *   creators: { monthlyActive, monthlyActivePosted, monthlyRate,
 *               allUsers, allUsersPosted, allTimeRate } }
 */
export const getStickiness = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/stickiness", {
    params,
  });
  return res.data;
};

/**
 * Cohort retention (Conversion page, Retention tab): weekly cohorts with
 * bounded day 1/7/14/28 retention (active exactly N days after day 0).
 * Day 0 = 5th vote (population "activated") or signup (population "all").
 * params: { timezone, weeks?, population? }
 * response data: { timezone, population, offsets,
 *   cohorts: [{ weekStart, weekEnd, size,
 *     retention: { d1: { returned, eligible, rate }, d7, d14, d28 } }]
 *     (newest first),
 *   overall: { d1, d7, d14, d28 } }
 */
export const getRetention = async (params) => {
  const res = await AxiosInstance.get("/analytics/admin/retention", {
    params,
  });
  return res.data;
};

/**
 * Personalized feed experiment results (Reports > Experiments): one run
 * ({ runId? }, default latest) overall, over time, by session number and
 * by adaptation, plus the run list and the run's config changes.
 */
export const getFeedPersonalizationExperiment = async (params) => {
  const res = await AxiosInstance.get(
    "/analytics/admin/experiments/feed-personalization",
    {
      params,
    }
  );
  return res.data;
};

/**
 * Update the experiment: { enabled?, allocation?, learningRate?,
 * maxMultiplier?, halfLifeDays?, signalWeights? }
 */
export const updateFeedPersonalizationConfig = async (body) => {
  const res = await AxiosInstance.put(
    "/analytics/admin/experiments/feed-personalization/config",
    body
  );
  return res.data;
};

/** One user's learned feed multipliers. */
export const getFeedPersonalizationUser = async (userId) => {
  const res = await AxiosInstance.get(
    `/analytics/admin/experiments/feed-personalization/users/${userId}`
  );
  return res.data;
};

/** Read-only experiment tokens (list; create returns the token once; revoke). */
export const listExperimentReadTokens = async () => {
  const res = await AxiosInstance.get("/analytics/admin/experiments/read-tokens");
  return res.data;
};

export const createExperimentReadToken = async (body) => {
  const res = await AxiosInstance.post(
    "/analytics/admin/experiments/read-tokens",
    body
  );
  return res.data;
};

export const revokeExperimentReadToken = async (id) => {
  const res = await AxiosInstance.delete(
    `/analytics/admin/experiments/read-tokens/${id}`
  );
  return res.data;
};
